import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import figurBin from "@/assets/grunder-figur-bin.png";
import figurSpade from "@/assets/grunder-figur-spade.png";
import { Button } from "@/components/ui/button";
import { GRUNDERLOFTET_AKTIV } from "@/lib/grunderloftet";

const STORAGE_KEY = "grunderloftet-skjult";

/**
 * Kampanjeramme for "Gründerløftet".
 * Ligger som et fast overlegg i kantene av siden, aldri over innholdet i midten.
 */
const GrunderloftetFrame = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY) === "1") return;
    const timer = window.setTimeout(() => setVisible(true), 600);
    return () => window.clearTimeout(timer);
  }, []);

  const skjul = () => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignorer */
    }
  };

  if (!GRUNDERLOFTET_AKTIV || !visible || pathname === "/grunderloftet") return null;

  return (
    <>
      {pathname !== "/" && (
        <div className="pointer-events-auto fixed bottom-3 right-3 z-[45] flex items-center gap-1 rounded-lg border border-campaign-lime bg-campaign-deep p-1 text-campaign-cream shadow-lg sm:hidden">
          <Link to="/grunderloftet" className="px-2 py-2 text-xs font-semibold">Gründerløftet <span className="text-campaign-lime">· 5 plasser</span></Link>
          <Button variant="ghost" size="icon" onClick={skjul} aria-label="Lukk kampanjen" className="h-8 w-8 text-campaign-lime hover:bg-campaign-lime/15 hover:text-campaign-lime"><X size={16} /></Button>
        </div>
      )}
    <div
      className="pointer-events-none fixed inset-0 z-[45] hidden animate-fade-in select-none sm:block"
    >
      {/* Grønn ramme langs alle kanter */}
      <div
        className="absolute inset-0 rounded-[26px]"
        style={{
          boxShadow:
            "inset 0 0 0 5px hsl(var(--campaign-deep)), inset 0 0 0 11px hsl(var(--campaign-lime) / 0.85), inset 0 0 0 13px hsl(var(--campaign-cream) / 0.9)",
        }}
      />

      {/* Organiske hjørneformer */}
      <svg
        className="absolute -left-10 -top-10 h-40 w-40 opacity-70 md:h-56 md:w-56"
        viewBox="0 0 200 200"
        fill="none"
      >
        <path
          d="M0 0h140c-10 40-48 46-70 64C44 84 44 130 0 150V0Z"
          fill="hsl(var(--campaign-lime))"
          opacity="0.75"
        />
      </svg>
      <svg
        className="absolute -bottom-10 -right-10 h-40 w-40 opacity-70 md:h-56 md:w-56"
        viewBox="0 0 200 200"
        fill="none"
      >
        <path
          d="M200 200H60c10-40 48-46 70-64 26-20 26-66 70-86v150Z"
          fill="hsl(var(--campaign-green))"
          opacity="0.6"
        />
      </svg>

      {/* Loddrett kampanjenavn på venstre kant (kun store skjermer) */}
      <div className="absolute left-[18px] top-1/2 hidden -translate-y-1/2 xl:block">
        <span
          className="block whitespace-nowrap text-[0.7rem] font-extrabold uppercase tracking-[0.4em]"
          style={{
            writingMode: "vertical-rl",
            color: "hsl(var(--campaign-deep))",
          }}
        >
          Gründerløftet 2026
        </span>
      </div>

      {/* Figurer som titter opp fra bunnkanten */}
      <img
        src={figurBin}
        alt=""
        width={560}
        height={818}
        loading="lazy"
        className="animate-grunder-peek absolute -bottom-1 left-1 w-[62px] origin-bottom drop-shadow-sm sm:left-3 sm:w-[86px] lg:w-[108px]"
      />
      <img
        src={figurSpade}
        alt=""
        width={440}
        height={1002}
        loading="lazy"
        className="animate-grunder-sway absolute -bottom-1 left-[64px] hidden w-[56px] origin-bottom drop-shadow-sm sm:left-[92px] sm:block sm:w-[68px] lg:left-[118px] lg:w-[84px]"
      />


      {/* Kampanjeboks nede til høyre */}
      <div className="pointer-events-auto absolute bottom-4 left-1/2 w-[min(92vw,420px)] -translate-x-1/2 sm:left-auto sm:right-6 sm:w-auto sm:max-w-[330px] sm:translate-x-0">
        <div
          className="relative rounded-2xl px-4 py-3 pr-9 shadow-lg sm:px-5 sm:py-4 sm:pr-10"
          style={{
            background: "hsl(var(--campaign-deep))",
            color: "hsl(var(--campaign-cream))",
            border: "2px solid hsl(var(--campaign-lime))",
          }}
        >
          <button
            type="button"
            onClick={skjul}
            aria-label="Lukk kampanjen"
            className="absolute right-2 top-2 rounded-full p-1 transition-opacity hover:opacity-70"
            style={{ color: "hsl(var(--campaign-lime))" }}
          >
            <X className="h-4 w-4" />
          </button>

          <span
            className="animate-grunder-glow mb-1 block text-[0.6rem] font-bold uppercase tracking-[0.25em]"
            style={{ color: "hsl(var(--campaign-lime))" }}
          >
            Kun 5 plasser
          </span>
          <p className="font-display text-lg font-extrabold leading-tight sm:text-xl">
            Gründerløftet
          </p>
          <p className="mt-1 text-xs leading-snug opacity-90 sm:text-sm">
            Norges neste gründere fortjener en profesjonell start. Søknaden er
            åpen nå.
          </p>
          <Link
            to="/grunderloftet"
            className="mt-3 inline-flex items-center rounded-full px-4 py-2 text-xs font-bold transition-transform hover:scale-105 sm:text-sm"
            style={{
              background: "hsl(var(--campaign-lime))",
              color: "hsl(var(--campaign-deep))",
            }}
          >
            Søk nå
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default GrunderloftetFrame;
